import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  HelpCircle,
  X,
  Send,
  Camera,
  Loader2,
  Bot,
  User,
  Upload,
  ChevronDown,
  RotateCcw,
  Mail,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, fetchWithAuth } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";

const HELP_AREAS = [
  { id: "getting-started", label: "Getting Started" },
  { id: "ai-quotes", label: "AI Quotes" },
  { id: "templates", label: "Templates" },
  { id: "cost-library", label: "Cost Library" },
  { id: "quote-editor", label: "Quote Editor" },
  { id: "pdf-export", label: "PDF Export" },
  { id: "pricing-plans", label: "Pricing & Plans" },
  { id: "referrals", label: "Referrals" },
  { id: "settings", label: "Settings" },
  { id: "clients", label: "Clients" },
];

const STARTER_SUGGESTIONS: Record<string, string[]> = {
  "getting-started": [
    "How do I create my first quote?",
    "What\u2019s the difference between Auto, Template, and Manual quotes?",
    "How do I set up my company profile?",
  ],
  "ai-quotes": [
    "What files can I upload for an Auto Quote?",
    "How does Auto Quote work out the prices?",
    "My Auto Quote is missing some items \u2014 what should I do?",
  ],
  "templates": [
    "Which project types have templates?",
    "Can I edit a template quote once it\u2019s created?",
    "During the free trial, do Template quotes count towards the shared 5-quote allowance?",
  ],
  "cost-library": [
    "How do I set my own rates?",
    "Can I add custom items not in the standard library?",
    "How do Learned Adjustments work?",
  ],
  "quote-editor": [
    "How do I add a new line item to a quote?",
    "How do I move an item to another section?",
    "How do I set assumptions and exclusions?",
  ],
  "pdf-export": [
    "How do I export a quote as a PDF?",
    "How do I add my company logo to PDFs?",
    "Can I send the PDF directly to a client?",
  ],
  "pricing-plans": [
    "What\u2019s the difference between Lite, Starter, and Pro?",
    "How do I upgrade my plan?",
    "What happens at the end of my free trial?",
  ],
  "referrals": [
    "How does the referral programme work?",
    "How do I withdraw my referral earnings?",
    "How much can I earn per referral?",
  ],
  "settings": [
    "How do I update my company details?",
    "How do I change my subscription?",
    "How do I set my hourly rate?",
  ],
  "clients": [
    "How do I add a new client?",
    "Can I see all quotes for a specific client?",
    "How do I edit a client\u2019s details?",
  ],
};

const CONTACT_KEYWORDS = [
  "speak to", "talk to", "human", "advisor", "real person", "someone",
  "contact", "support team", "help desk", "phone", "call",
];

type Message = {
  role: "user" | "assistant";
  content: string;
  screenshot?: string;
};

type Mode = "chat" | "contact";

export function HelpAgent() {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("chat");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingScreenshot, setPendingScreenshot] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showAreas, setShowAreas] = useState(true);
  const [showContactPrompt, setShowContactPrompt] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactSent, setContactSent] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [location] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && textareaRef.current && mode === "chat") {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, mode]);

  const contactMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/feedback", {
        type: "contact",
        page: location,
        message: contactMessage.trim(),
      }),
    onSuccess: () => {
      setContactSent(true);
      setContactMessage("");
    },
    onError: () => toast({ title: "Failed to send message", variant: "destructive" }),
  });

  const captureScreen = async () => {
    setIsCapturing(true);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const video = document.createElement("video");
      video.srcObject = stream;
      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => { video.play(); resolve(); };
      });
      await new Promise((r) => setTimeout(r, 200));
      const scale = 0.6;
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(video.videoWidth * scale);
      canvas.height = Math.floor(video.videoHeight * scale);
      canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
      stream.getTracks().forEach((t) => t.stop());
      setPendingScreenshot(canvas.toDataURL("image/jpeg", 0.65));
    } catch (err: any) {
      if (err?.name !== "AbortError" && err?.name !== "NotAllowedError") {
        toast({ title: "Couldn\u2019t capture screen", description: "Try uploading a screenshot manually instead.", variant: "destructive" });
      }
    } finally {
      setIsCapturing(false);
    }
  };

  const sendMessage = async (overrideMessage?: string) => {
    const text = (overrideMessage ?? input).trim();
    if (!text && !pendingScreenshot) return;

    const lowerText = text.toLowerCase();
    const wantsContact = CONTACT_KEYWORDS.some(kw => lowerText.includes(kw));

    const userMsg: Message = {
      role: "user",
      content: text || "What do you see on my screen?",
      screenshot: pendingScreenshot ?? undefined,
    };

    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setPendingScreenshot(null);
    setShowAreas(false);
    setIsLoading(true);

    try {
      const response = await fetchWithAuth("/api/help/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMsg.content,
          history: messages.slice(-8),
          screenshot: userMsg.screenshot,
          area: selectedArea,
          page: location,
        }),
      });
      if (response.status === 401) {
        toast({
          title: "Please sign in again",
          description: "Your session has expired. Sign in again to continue using help chat.",
          variant: "destructive",
        });
        return;
      }
      if (!response.ok) throw new Error("Failed to get response");
      const data = await response.json();
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (wantsContact) setShowContactPrompt(true);
    } catch {
      toast({ title: "Couldn\u2019t get a response", description: "Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast({ title: "Image too large", description: "Please use an image under 4MB.", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setPendingScreenshot(ev.target?.result as string);
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetChat = () => {
    setMessages([]);
    setSelectedArea(null);
    setShowAreas(true);
    setInput("");
    setPendingScreenshot(null);
    setShowContactPrompt(false);
    setContactSent(false);
  };

  const openContact = () => {
    setMode("contact");
    setContactSent(false);
    setContactMessage("");
  };

  const suggestions = selectedArea ? STARTER_SUGGESTIONS[selectedArea] ?? [] : [];

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="button-help-agent-toggle"
        title="Get help"
      >
        {isOpen ? <X className="h-5 w-5" /> : <HelpCircle className="h-5 w-5" />}
      </Button>

      {isOpen && createPortal(
        <div
          className="fixed top-14 right-4 z-[200] w-[390px] rounded-xl border text-card-foreground shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: "80vh", backgroundColor: "hsl(var(--card))" }}
          data-testid="help-agent-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-primary/5 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              {mode === "contact" && (
                <button
                  onClick={() => setMode("chat")}
                  className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mr-1"
                  data-testid="button-help-back"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
              )}
              <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                {mode === "contact" ? <Mail className="h-4 w-4 text-primary" /> : <Bot className="h-4 w-4 text-primary" />}
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-none">
                  {mode === "contact" ? "Contact Support" : "The Proper Tool Assistant"}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {mode === "contact" ? "We\u2019ll reply to your email" : "Ask me anything about the app"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {mode === "chat" && (
                <button
                  onClick={openContact}
                  className="h-7 px-2 rounded-md flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Contact support"
                  data-testid="button-help-contact"
                >
                  <Mail className="h-3 w-3" />
                  Contact
                </button>
              )}
              {mode === "chat" && messages.length > 0 && (
                <button
                  onClick={resetChat}
                  className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Start new conversation"
                  data-testid="button-help-reset"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title="Close"
                data-testid="button-help-close"
                aria-label="Close assistant"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* ── CONTACT MODE ── */}
          {mode === "contact" && (
            <div className="flex-1 p-5 flex flex-col">
              {contactSent ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
                  <CheckCircle2 className="h-10 w-10 text-primary" />
                  <p className="font-semibold">Message sent!</p>
                  <p className="text-sm text-muted-foreground">Our team will reply to your email as soon as possible.</p>
                  <Button variant="outline" size="sm" onClick={() => { setMode("chat"); setContactSent(false); }} data-testid="button-contact-done">
                    Back to assistant
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    Can&apos;t find what you need? Send us a message and we&apos;ll get back to you via email.
                  </p>
                  <Textarea
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="Describe your question or issue..."
                    className="resize-none text-sm flex-1 min-h-[140px]"
                    rows={6}
                    data-testid="input-contact-message"
                  />
                  <Button
                    className="mt-4 w-full"
                    onClick={() => contactMutation.mutate()}
                    disabled={!contactMessage.trim() || contactMutation.isPending}
                    data-testid="button-contact-send"
                  >
                    {contactMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Send Message
                  </Button>
                </>
              )}
            </div>
          )}

          {/* ── CHAT MODE ── */}
          {mode === "chat" && (
            <>
              {/* Topic chips */}
              <div className="flex-shrink-0 border-b">
                <button
                  onClick={() => setShowAreas(!showAreas)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  data-testid="button-help-areas-toggle"
                >
                  <span className="font-medium">
                    {selectedArea
                      ? `Topic: ${HELP_AREAS.find((a) => a.id === selectedArea)?.label}`
                      : "Select a topic (optional)"}
                  </span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAreas ? "rotate-180" : ""}`} />
                </button>
                {showAreas && (
                  <div className="px-3 pb-3">
                    <div className="flex flex-wrap gap-1.5">
                      {HELP_AREAS.map((area) => (
                        <button
                          key={area.id}
                          onClick={() => setSelectedArea(selectedArea === area.id ? null : area.id)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            selectedArea === area.id
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                          }`}
                          data-testid={`button-help-area-${area.id}`}
                        >
                          {area.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0"
                data-testid="help-messages"
              >
                {messages.length === 0 && (
                  <div className="py-4">
                    <div className="text-center mb-4">
                      <HelpCircle className="h-9 w-9 text-primary/25 mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Hi! I know The Proper Tool inside and out. Pick a topic above, use a suggestion below, or just type your question.
                      </p>
                    </div>
                    {suggestions.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs text-muted-foreground font-medium mb-2">Common questions:</p>
                        {suggestions.map((s, i) => (
                          <button
                            key={i}
                            onClick={() => sendMessage(s)}
                            className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 text-muted-foreground hover:text-foreground transition-colors"
                            data-testid={`button-help-suggestion-${i}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                    {!selectedArea && (
                      <div className="space-y-1.5 mt-3">
                        <p className="text-xs text-muted-foreground font-medium mb-2">Or try asking:</p>
                        {[
                          "How do I create a quote?",
                          "How does the AI pricing work?",
                          "How do I export a PDF?",
                        ].map((s, i) => (
                          <button
                            key={i}
                            onClick={() => sendMessage(s)}
                            className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 text-muted-foreground hover:text-foreground transition-colors"
                            data-testid={`button-help-default-suggestion-${i}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot className="h-3.5 w-3.5 text-primary" />
                      </div>
                    )}
                    <div className="max-w-[85%] space-y-1.5">
                      {msg.screenshot && (
                        <img src={msg.screenshot} alt="Screen capture" className="rounded-lg border w-full" />
                      )}
                      <div
                        className={`rounded-xl px-3 py-2 text-sm leading-relaxed ${
                          msg.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-foreground"
                        }`}
                      >
                        {msg.content}
                      </div>
                    </div>
                    {msg.role === "user" && (
                      <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User className="h-3.5 w-3.5 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="bg-muted rounded-xl px-3 py-2.5 flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                )}

                {showContactPrompt && !isLoading && (
                  <div className="flex justify-start">
                    <div className="rounded-xl border px-4 py-3 text-sm space-y-2 max-w-[85%]" style={{ backgroundColor: "hsl(var(--card))" }}>
                      <p className="text-muted-foreground">Still need help? Send a message to our team.</p>
                      <Button size="sm" variant="outline" onClick={openContact} data-testid="button-help-open-contact">
                        <Mail className="h-3.5 w-3.5 mr-1.5" />
                        Contact Support
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Screenshot preview */}
              {pendingScreenshot && (
                <div className="px-4 pb-2 flex-shrink-0">
                  <div className="relative inline-block">
                    <img src={pendingScreenshot} alt="Captured screen" className="h-16 rounded-md border object-cover" />
                    <button
                      onClick={() => setPendingScreenshot(null)}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow"
                      data-testid="button-remove-screenshot"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Screenshot attached \u2014 add your question below</p>
                </div>
              )}

              {/* Input area */}
              <div className="p-3 border-t flex-shrink-0 bg-background/50">
                <div className="flex gap-2 items-end">
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={captureScreen}
                      disabled={isCapturing}
                      title="Capture your screen"
                      className="h-9 w-9 rounded-md border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors disabled:opacity-50"
                      data-testid="button-capture-screen"
                    >
                      {isCapturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-testid="input-help-file" />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      title="Upload a screenshot"
                      className="h-9 w-9 rounded-md border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
                      data-testid="button-upload-screenshot"
                    >
                      <Upload className="h-4 w-4" />
                    </button>
                  </div>
                  <Textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    placeholder="Ask a question..."
                    className="resize-none text-sm flex-1 min-h-[36px] max-h-[100px] py-2"
                    rows={1}
                    data-testid="input-help-message"
                  />
                  <Button
                    size="icon"
                    onClick={() => sendMessage()}
                    disabled={(!input.trim() && !pendingScreenshot) || isLoading}
                    className="h-9 w-9 flex-shrink-0"
                    data-testid="button-send-help"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
